import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiMail, FiEdit2, FiTrash2, FiUser } from 'react-icons/fi';
import { EmptyState } from '../../components/common';
import { TABLE, ACTIONS } from '../../constants/labels';
import { fmtCurrency } from '../../utils/formatNumber';
import { formatDate } from '../../utils/dateUtils';
import { PAYMENT_LABEL } from './receiptsConfig';
import styles from './Receipts.module.css';

const PAGE_SIZE = 50;

export default function ReceiptsTable({
  receipts, members, totalAmount,
  onDownload, onEmail, onEdit, onCancelClick,
  isDerasarAdmin
}) {
  const navigate = useNavigate();
  const donorName = (r) => r.member?.name || (r.userId ? (members.find((m) => m.id === r.userId)?.name || null) : null);

  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [receipts]);
  const totalPages = Math.ceil(receipts.length / PAGE_SIZE);
  const visibleReceipts = receipts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
    <div className="card">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>{TABLE.receiptNo}</th>
              <th>{TABLE.date}</th>
              <th>Member</th>
              <th>Offerings</th>
              <th>Mode</th>
              <th className={styles.amtRight}>{TABLE.amount}</th>
              <th>Gold g</th>
              <th>{TABLE.narration}</th>
              <th>{TABLE.actions}</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 && (
              <tr>
                <td colSpan={10}>
                  <EmptyState message={TABLE.noResults(PAYMENT_LABEL)} />
                </td>
              </tr>
            )}
            {visibleReceipts.map((r, idx) => (
              <tr key={r.id}>
                <td>{page * PAGE_SIZE + idx + 1}</td>
                <td>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '2px 6px', fontFamily: 'monospace', fontWeight: 600 }}
                    onClick={() => navigate(`/accounting/receipts/${r.id}`)}
                    title="View receipt detail"
                  >
                    {r.receiptNo}
                  </button>
                  {r.manualReceiptNo && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 2 }}>
                      Ref: {r.manualReceiptNo}
                    </div>
                  )}
                </td>
                <td>{formatDate(r.receiptDate)}</td>
                <td>
                  {r.receiptType === 'anonymous' ? (
                    <>
                      {r.donorName || 'Shah A/C'}
                      <span className={styles.walkInBadge}>
                        <FiUser size={10} /> Walk-in
                      </span>
                    </>
                  ) : (
                    donorName(r) || <em className="text-muted">—</em>
                  )}
                </td>
                <td>
                  {r.pledgeSettlements?.length > 0 ? (
                    <span className="badge badge-info">
                      {r.pledgeSettlements.length} offering{r.pledgeSettlements.length > 1 ? 's' : ''}
                    </span>
                  ) : '—'}
                </td>
                <td>
                  <span className="badge badge-pending" style={{ textTransform: 'capitalize' }}>
                    {r.paymentMode?.replace('_', ' ') || '—'}
                  </span>
                </td>
                <td className={styles.amtRight}>{fmtCurrency(r.amountInr)}</td>
                <td>{r.goldGrams ? `${r.goldGrams}g` : '—'}</td>
                <td style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.narration}>
                  {r.narration || '—'}
                </td>
                <td>
                  <div className={styles.actions}>
                    <button className="btn btn-ghost btn-sm" title={ACTIONS.download} onClick={() => onDownload(r)}>
                      <FiDownload />
                    </button>
                    {r.userId && (
                      <button className="btn btn-ghost btn-sm" title={ACTIONS.email} onClick={() => onEmail(r)}>
                        <FiMail />
                      </button>
                    )}
                    {isDerasarAdmin && r.receiptType !== 'online' && (
                      <button className="btn btn-ghost btn-sm" title={ACTIONS.edit} onClick={() => onEdit(r)}>
                        <FiEdit2 />
                      </button>
                    )}
                    {isDerasarAdmin && (
                      <button className="btn btn-danger btn-sm" title={ACTIONS.delete} onClick={() => onCancelClick(r)}>
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {receipts.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6}><strong>{TABLE.totals}</strong></td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalAmount)}</strong></td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
      {totalPages > 1 && (
        <div className={styles.paginationBar}>
          <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>← Prev</button>
          <span>Page {page + 1} of {totalPages} &nbsp;·&nbsp; {receipts.length} total</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>Next →</button>
        </div>
      )}
    </>
  );
}
